import React, { FC, useEffect } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import usePlayerChangeController from 'application/hooks/usePlayerChangeController';
import { useCompanyId } from 'application/providers/AccountProvider';
import { useBillingRepositoryInjector } from 'application/providers/DIContainerProvider';

import { DeviceTypes, PaymentKind } from 'domain/api/graphql/generated';

import Input from '@zvuk-b2b/react-uikit/ui/Input';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { TEXTS } from './texts';
import { SelectPlayerFlowType } from '../../types';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';

import styles from './index.module.scss';

const DEVICE_IMAGE_PATH = '/assets/img/app/video/tv.png';

const BindVideoModal: FC<ModalProps[ModalTypes.VIDEO_PLAYER_BIND]> = ({ openModal, replaceModal, close }) => {
  const { flowType, currentPoint } = useOnboardingContext();
  const { setInFlow, clearCurrentPoint } = useOnboardingActionContext();
  const companyId = useCompanyId();

  const { watchBillingInfo } = useBillingRepositoryInjector();
  const { hasInactiveLicenses, selectedPaymentKind } = watchBillingInfo(companyId);

  const isShowPaymentModal = () => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const onSuccessCallback = () => {
    setInFlow(false);

    if (flowType === SelectPlayerFlowType.CREATE_POINT || flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (isShowPaymentModal()) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {});
    }
  };

  const { bindingCode, setBindingCode, bindingCodeError, handleUpdateClick, setToPlayerType } =
    usePlayerChangeController({
      pointId: currentPoint?.id || '',
      fromPlayerType: DeviceTypes.Ios, // TODO: don't matter which mobile type will be passed
      onSuccessCallback,
      isVideoMode: true,
    });

  useEffect(() => {
    setToPlayerType('APP');
  }, []);

  const handleCancel = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      title={TEXTS.MODAL_TITLE}
      className={styles.MobilePlayerFlowModal}
      contentClassName={styles.MobilePlayerFlowModalContent}
      containerActionButtonsClassName={styles.MobilePlayerFlowModalActionButtons}
      actionButtons={[
        <Button
          key="button_bind_video_modal_success"
          elementId="button_bind_video_modal_success"
          type={ButtonType.PRIMARY}
          isDisabled={bindingCode.length < 4}
          onClick={handleUpdateClick}>
          {TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_bind_video_modal_cancel"
          elementId="button_bind_video_modal_cancel"
          type={ButtonType.SECONDARY}
          onClick={() => {
            replaceModal(ModalTypes.DOWNLOAD_TV_APP, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <div className={styles.MobilePlayerFlowModalDescription}>{TEXTS.TITLE_DESCRIPTION}</div>

      <div className={styles.MobilePlayerFlowModalDownloadContent}>
        <Input
          elementId="button_bind_video_modal_code_input"
          onChange={setBindingCode}
          value={bindingCode}
          placeholder={TEXTS.CODE_BIND_PLACEHOLDER}
          maxLength={4}
          label={TEXTS.CODE_BIND_TITLE}
          error={bindingCodeError}
          containerClassName={styles.MobilePlayerFlowModalInput}
        />

        <img
          src={DEVICE_IMAGE_PATH}
          alt=""
          loading="lazy"
          className={styles.MobilePlayerFlowModalImageBind}
        />
      </div>
    </ModalScreen>
  );
};

export default BindVideoModal;
